import { useEffect, useRef, useState } from 'react';
import { Alert, Button, Modal, Space, Spin, Typography, Upload, message } from 'antd';
import {
  CloseOutlined,
  DownloadOutlined,
  InboxOutlined,
  InfoCircleOutlined,
  FileExcelFilled,
  FileWordFilled,
} from '@ant-design/icons';
import { v4 as uuid } from 'uuid';
import { articleApi } from '@/axios/company';
import {
  articleImportApi,
  type ArticleImportRules,
  type SelectedArticleImportFile,
} from '@/axios/articleImport';
import { addImportFiles, buildBatchFormData, getImportSummary, mergeImportResult, type ArticleImportViewResult } from './articleImportFiles';
import styles from './index.module.less';

/** 独立受控上传入口，提交前取消与汇总确认使用不同回调。 */
export interface ArticleUploadModalProps {
  companyId: string;
  companyName?: string;
  onClose: () => void;
  onConfirm: (result: ArticleImportViewResult) => void;
}

/**
 * 从已明确拒绝的业务响应提取展示信息。
 * @param {unknown} error 请求异常。
 * @returns {string} 安全业务文案或通用错误。
 */
function errorMessage(error: unknown): string {
  const response = error as { err_msg?: string; message?: string };
  return response?.err_msg || response?.message || '请求失败，请稍后重试';
}

/**
 * 受控管理文件选择、一次同步上传和最终汇总，确认后交由父页刷新。
 * @param {ArticleUploadModalProps} props 当前企业、取消关闭及结果确认回调。
 * @returns {JSX.Element} 上传与汇总两个独立弹窗。
 */
export default function ArticleUploadModal({ companyId, companyName, onClose, onConfirm }: ArticleUploadModalProps): JSX.Element {
  const [rules, setRules] = useState<ArticleImportRules>();
  const [rulesError, setRulesError] = useState('');
  const [files, setFiles] = useState<SelectedArticleImportFile[]>([]);
  const [hashing, setHashing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [result, setResult] = useState<ArticleImportViewResult>();
  const filesRef = useRef<SelectedArticleImportFile[]>([]);
  const active = useRef(true);
  const busyRef = useRef(false);
  const hashingRef = useRef(false);
  const resultConfirmed = useRef(false);
  const [modal, modalContext] = Modal.useModal();

  /**
   * 加载服务端规则，失败时禁止提交并允许用户重新查询。
   * @returns {Promise<void>} 规则加载结束。
   */
  async function loadRules(): Promise<void> {
    setRulesError('');
    try {
      const response = await articleImportApi.getRules();
      if (active.current) {
        setRules(response);
      }
    } catch {
      if (active.current) {
        setRulesError('上传规则暂时无法获取，请重试');
      }
    }
  }

  useEffect(() => {
    active.current = true;
    void loadRules();
    return () => {
      active.current = false;
    };
  }, []);

  /**
   * 顺序读取本次选择，不解析正文或提前上传。
   * @param {File[]} incoming 按选择顺序排列的原文件。
   * @returns {Promise<void>} 摘要和本地校验完成。
   */
  async function selectFiles(incoming: File[]): Promise<void> {
    if (!rules || submitted || busyRef.current || hashingRef.current) {
      return;
    }
    hashingRef.current = true;
    setHashing(true);
    try {
      const next = await addImportFiles(filesRef.current, incoming, rules);
      if (!active.current) {
        return;
      }
      filesRef.current = next.files;
      setFiles(next.files);
      if (next.rejected.length) {
        message.warning({ content: next.rejected.join('；'), duration: 6 });
      }
    } finally {
      hashingRef.current = false;
      if (active.current) {
        setHashing(false);
      }
    }
  }

  /**
   * 移除一个已选文件，不影响同名但不同内容的其他项。
   * @param {string} fileId 不可变文件 UUID。
   * @returns {void} 更新当前清单。
   */
  function removeFile(fileId: string): void {
    if (busyRef.current || submitted || hashingRef.current) {
      return;
    }
    filesRef.current = filesRef.current.filter((entry) => entry.fileId !== fileId);
    setFiles(filesRef.current);
  }

  /**
   * 使用新批次身份同步等待一次最终响应，不查询、不自动重传。
   * @param {SelectedArticleImportFile[]} selectedFiles 本次真正上传的原文件。
   * @param {ArticleImportViewResult | undefined} previous 重试前结果，首次提交时为空。
   * @returns {Promise<void>} 请求结束后显示完整结果或本轮待核实文件。
   */
  async function uploadFiles(selectedFiles: SelectedArticleImportFile[], previous?: ArticleImportViewResult): Promise<void> {
    busyRef.current = true;
    setBusy(true);
    setSubmitted(true);
    const manifest = { batchId: uuid(), companyId, files: selectedFiles };
    try {
      const response = await articleImportApi.submit(buildBatchFormData(manifest, selectedFiles));
      if (active.current) {
        setResult(mergeImportResult(previous, response, manifest, filesRef.current));
      }
    } catch (error) {
      if (!active.current) {
        return;
      }
      // 仅10004证明任何保存前已拒绝，允许首次恢复选择或保留原失败项等待手动重试。
      if ((error as { err_no?: unknown })?.err_no === 10004) {
        if (!previous) {
          setSubmitted(false);
        }
        message.error(errorMessage(error));
      } else {
        // 10000、超时或连接断开均不能证明未保存，只将本轮文件转为待核实。
        setResult(mergeImportResult(previous, undefined, manifest, filesRef.current));
      }
    } finally {
      busyRef.current = false;
      if (active.current) {
        setBusy(false);
      }
    }
  }

  /**
   * 锁定当前完整清单后提交，等待同一个 POST 返回最终结果。
   * @returns {Promise<void>} 本批处理结束；用户确认汇总前不通知父页刷新。
   */
  async function submit(): Promise<void> {
    if (!rules || busyRef.current || hashingRef.current || submitted || !filesRef.current.length) {
      return;
    }
    await uploadFiles(filesRef.current);
  }

  /**
   * 一次重传全部明确可重试失败项，使用新批次并保留原 fileId 和原文件。
   * @returns {Promise<void>} 合并本轮结果，成功、不可重试及待核实项不重传。
   */
  async function retryFailed(): Promise<void> {
    if (busyRef.current || !result) {
      return;
    }
    const retryable = result.failedFiles.filter((entry) => entry.retryable);
    if (!retryable.length) {
      return;
    }
    const selected = retryable.map((entry) => filesRef.current.find((file) => file.fileId === entry.fileId));
    if (selected.some((entry) => !entry?.file)) {
      message.warning('原始文件已丢失，请核实文章列表后重新发起导入');
      return;
    }
    await uploadFiles(selected as SelectedArticleImportFile[], result);
  }

  /**
   * 提交前保护尚未上传的文件，处理中禁止关闭。
   * @returns {void} 无文件直接关闭，有文件显示放弃确认。
   */
  function requestClose(): void {
    if (busyRef.current || submitted || hashingRef.current) {
      return;
    }
    if (!filesRef.current.length) {
      onClose();
      return;
    }
    modal.confirm({
      title: '关闭后将清空尚未上传的文件，是否确认关闭？',
      okText: '确认关闭',
      cancelText: '继续上传',
      onOk: onClose,
    });
  }

  /**
   * 下载既有 Excel 模板，及时释放下载 URL。
   * @returns {Promise<void>} 模板下载请求完成。
   */
  async function downloadTemplate(): Promise<void> {
    try {
      const blob = await articleApi.downloadUploadTemplate();
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = '文章导入模板.xlsx';
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      URL.revokeObjectURL(url);
    } catch (error) {
      message.error(errorMessage(error));
    }
  }

  /**
   * 仅在用户确认汇总后通知父页，重复点击不能重复刷新列表及成本。
   * @returns {void} 交付最终结果；提交前取消不经过此路径。
   */
  function confirmResult(): void {
    if (busyRef.current || !result || resultConfirmed.current) {
      return;
    }
    resultConfirmed.current = true;
    onConfirm(result);
  }

  const locked = submitted || busy || hashing;
  const failed = result?.failedFiles || [];
  const warnings = result?.warningFiles || [];
  const unknown = result?.unknownFiles || [];
  const canRetry = failed.some((entry) => entry.retryable);

  return (
    <>
      {modalContext}
      <Modal
        open
        title="上传文章"
        width={720}
        maskClosable={false}
        keyboard={!locked}
        closable={!locked}
        onCancel={requestClose}
        footer={
          <div className={styles.uploadFooter}>
            {/* 直接使用有效清单计数，追加、去重、移除与处理中均保持一致。 */}
            <span className={styles.uploadFileCount} role="status" aria-label="已选择文件数量">
              已选择 <strong>{files.length}</strong> 个文件
            </span>
            <Space className={styles.uploadFooterActions}>
              <Button autoInsertSpace={false} disabled={locked} onClick={requestClose}>
                取消
              </Button>
              <Button
                type="primary"
                disabled={!rules || !files.length || locked}
                loading={busy}
                onClick={() => {
                  void submit();
                }}
              >
                确认上传
              </Button>
            </Space>
          </div>
        }
      >
        <div className={styles.uploadArticleModal}>
          <div className={styles.uploadCompany}>
            当前企业：<span>{companyName || '-'}</span>
          </div>
          <div className={styles.uploadNotice}>
            <InfoCircleOutlined />
            <span>支持 Excel、Word 混合批量导入。Word 一文件一篇，默认待发布；表格及其内容会被忽略。</span>
          </div>
          {rulesError && (
            <Alert
              type="warning"
              title={rulesError}
              action={
                <Button
                  size="small"
                  onClick={() => {
                    void loadRules();
                  }}
                >
                  重试获取规则
                </Button>
              }
            />
          )}
          <Button
            type="link"
            icon={<DownloadOutlined />}
            className={styles.uploadTemplateBtn}
            disabled={submitted || busy}
            onClick={() => {
              void downloadTemplate();
            }}
          >
            下载上传模板
          </Button>
          <Upload.Dragger
            className={styles.uploadDragger}
            accept={(rules?.supportedExtensions || ['.xlsx', '.xls', '.doc', '.docx']).join(',')}
            multiple
            disabled={locked || !rules}
            showUploadList={false}
            beforeUpload={(file, selection) => {
              if (file === selection[0]) {
                void selectFiles(selection);
              }
              return Upload.LIST_IGNORE;
            }}
          >
            <div className={styles.uploadDropContent}>
              <InboxOutlined className={styles.uploadDraggerIcon} />
              <Typography.Text className={styles.uploadDropTitle}>点击或拖拽 Excel、Word 文件到此处</Typography.Text>
              <Typography.Text type="secondary">
                支持 .xlsx / .xls / .doc / .docx，单文件最大 {rules ? rules.maxFileSizeBytes / 1024 / 1024 : 20}
                MB，每批最多 {rules?.maxFileCount || 30} 个
              </Typography.Text>
            </div>
          </Upload.Dragger>
          {hashing && <Typography.Paragraph role="status">正在校验文件并计算摘要…</Typography.Paragraph>}
          {!!files.length && (
            <section className={styles.uploadFilesCard} aria-label="已选择文件">
              <div className={styles.uploadFilesHeading}>已选择文件</div>
              <ul className={styles.uploadFileList}>
                {files.map((entry) => {
                  const excel = /\.xlsx?$/i.test(entry.filename);
                  const typeClass = excel ? styles.uploadExcel : styles.uploadWord;
                  return (
                    <li key={entry.fileId}>
                      {excel ? (
                        <FileExcelFilled aria-hidden className={`${styles.uploadFileIcon} ${typeClass}`} />
                      ) : (
                        <FileWordFilled aria-hidden className={`${styles.uploadFileIcon} ${typeClass}`} />
                      )}
                      <span className={styles.uploadFileName} title={entry.filename}>
                        {entry.filename}
                      </span>
                      <span className={`${styles.uploadFileType} ${typeClass}`}>{excel ? 'Excel' : 'Word'}</span>
                      <span className={styles.uploadFileStatus}>
                        <span className={busy ? styles.uploadProcessingDot : styles.uploadPendingDot} aria-hidden />
                        {busy ? '处理中' : submitted ? '已提交' : '待上传'}
                      </span>
                      <Button
                        aria-label={'移除 ' + entry.filename}
                        className={styles.uploadFileRemove}
                        type="text"
                        size="small"
                        icon={<CloseOutlined />}
                        disabled={locked}
                        onClick={() => removeFile(entry.fileId)}
                      />
                    </li>
                  );
                })}
              </ul>
            </section>
          )}
          {busy && !result && (
            <div className={styles.uploadProcessing} role="status">
              <Spin /> 正在处理，请稍候…
            </div>
          )}
          <div className={styles.uploadRules}>
            <div className={styles.uploadRulesTitle}>上传规则</div>
            <ol className={styles.uploadRulesList}>
              <li>可多选、拖拽和追加文件；同内容只保留先添加的文件，同名但不同内容允许上传。</li>
              <li>
                Excel
                沿用现有模板。标题、内容必填；平台、媒体、价格和发布网址选填；发布网址在文件内和当前企业下不可重复。
              </li>
              <li>
                Excel 有发布网址为已发布，无发布网址为待发布；Word
                使用文件名作为标题，保留文本、常见标题、加粗、列表和正文图片。
              </li>
              <li>Word 表格及其内容忽略并在成功汇总中提示，忽略后无有效正文则失败。</li>
              <li>一次提交全部文件，单文件失败不回滚其他成功文章。结束后查看汇总，点击“确定”后刷新文章列表和已发布成本。</li>
            </ol>
          </div>
        </div>
      </Modal>
      <Modal
        open={!!result}
        title="文章导入汇总"
        width={640}
        maskClosable={false}
        keyboard={false}
        closable={false}
        footer={
          <Space>
            {canRetry && (
              <Button
                disabled={busy}
                loading={busy}
                onClick={() => {
                  void retryFailed();
                }}
              >
                重试失败项
              </Button>
            )}
            <Button type="primary" autoInsertSpace={false} disabled={busy} onClick={confirmResult}>
              确定
            </Button>
          </Space>
        }
      >
        {busy ? (
          <div className={styles.uploadProcessing} role="status">
            <Spin /> 正在处理，请稍候…
          </div>
        ) : (
          result && (
            <>
              <Typography.Paragraph strong>{getImportSummary(result)}</Typography.Paragraph>
              {result.countsKnown && !!result.skippedArticleCount && (
                <Typography.Paragraph type="secondary">
                  Excel 已跳过 {result.skippedArticleCount} 篇文章
                </Typography.Paragraph>
              )}
              {!!failed.length && (
                <section>
                  <Typography.Title level={5}>失败文件</Typography.Title>
                  <ul className={styles.uploadSummaryList}>
                    {failed.map((entry) => (
                      <li key={entry.fileId}>
                        <Typography.Text strong>{entry.filename}</Typography.Text>
                        <div>
                          {entry.errorMessage}
                          {!entry.retryable && '，请修改文件后重新发起导入'}
                        </div>
                      </li>
                    ))}
                  </ul>
                </section>
              )}
              {!!warnings.length && (
                <section>
                  <Typography.Title level={5}>导入提示</Typography.Title>
                  <ul className={styles.uploadSummaryList}>
                    {warnings.map((entry) => (
                      <li key={entry.fileId}>
                        <Typography.Text strong>{entry.filename}</Typography.Text>：<span>{entry.warningMessage}</span>
                      </li>
                    ))}
                  </ul>
                </section>
              )}
              {!!unknown.length && (
                <section>
                  <Typography.Title level={5}>待核实文件</Typography.Title>
                  {result.countsKnown && <Alert type="warning" title="导入结果未确认，请核实文章列表后再操作" />}
                  <ul className={styles.uploadSummaryList}>
                    {unknown.map((entry) => (
                      <li key={entry.fileId}>
                        <Typography.Text strong>{entry.filename}</Typography.Text>
                        <div>{entry.errorMessage}</div>
                      </li>
                    ))}
                  </ul>
                </section>
              )}
            </>
          )
        )}
      </Modal>
    </>
  );
}
